import React from "react";
import { formatMoney } from "../format";

interface Totals {
    income: number;
    expenses: number;
    net: number;
    savingsRate: number | null;
}

interface SummaryTilesProps {
    current: Totals;
    previous: Totals;
    currency: string;
}

export function SummaryTiles({ current, previous, currency }: SummaryTilesProps) {
    const rate = (r: number | null) => (r === null ? "–" : `${Math.round(r * 100)}%`);

    return (
        <div className="fin-tiles">
            <Tile label="Income" value={formatMoney(current.income, currency)} change={change(current.income, previous.income)} goodWhenUp />
            <Tile label="Expenses" value={formatMoney(current.expenses, currency)} change={change(current.expenses, previous.expenses)} goodWhenUp={false} />
            <Tile label="Net balance" value={formatMoney(current.net, currency)} change={null} negative={current.net < 0} />
            <Tile
                label="Savings rate"
                value={rate(current.savingsRate)}
                change={null}
                hint={previous.savingsRate === null ? undefined : `${rate(previous.savingsRate)} last month`}
            />
        </div>
    );
}

function change(current: number, previous: number): number | null {
    return previous > 0 ? (current - previous) / previous : null;
}

interface TileProps {
    label: string;
    value: string;
    change: number | null;
    goodWhenUp?: boolean;
    negative?: boolean;
    hint?: string;
}

function Tile({ label, value, change: delta, goodWhenUp = true, negative, hint }: TileProps) {
    let deltaText: React.ReactNode = hint ? <span className="fin-tile-hint">{hint}</span> : null;
    if (delta !== null) {
        const up = delta >= 0;
        const good = up === goodWhenUp;
        // Arrow + words carry the meaning; color only reinforces it
        deltaText = (
            <span className={`fin-tile-delta ${good ? "good" : "bad"}`}>
                {up ? "▲" : "▼"} {Math.abs(Math.round(delta * 100))}% vs last month
            </span>
        );
    }
    return (
        <div className="fin-tile">
            <span className="fin-tile-label">{label}</span>
            <strong className={`fin-tile-value ${negative ? "negative" : ""}`}>{value}</strong>
            {deltaText}
        </div>
    );
}
